import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const RegisterForm = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'El nombre es requerido';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'El apellido es requerido';
        }

        if (!formData.email) {
            newErrors.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'El email no es válido';
        }

        if (!formData.password) {
            newErrors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirma tu contraseña';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        if (!formData.acceptTerms) {
            newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        
        try {
            await register({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password
            });
            toast.success('¡Cuenta creada exitosamente!');
            navigate('/');
        } catch (error) {
            console.error('Error en registro:', error);
            toast.error(error.message || 'Error al crear la cuenta');
        } finally {
            setLoading(false);
        }
    };

    const handleSocialRegister = (provider) => {
        toast.info(`Próximamente: Registro con ${provider}`);
    };

    return (
        <div className="register-form-container">
            <Card className="register-card">
                <div className="text-center mb-4">
                    <h2 className="text-3xl font-bold text-900 mb-2">
                        Crear Cuenta
                    </h2>
                    <p className="text-600">
                        Únete a nuestra comunidad
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field mb-4">
                                <label htmlFor="firstName" className="block text-900 font-medium mb-2">
                                    Nombre
                                </label>
                                <InputText
                                    id="firstName"
                                    placeholder="Tu nombre"
                                    value={formData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                    className={`w-full ${errors.firstName ? 'p-invalid' : ''}`}
                                />
                                {errors.firstName && (
                                    <small className="p-error block mt-1">
                                        {errors.firstName}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field mb-4">
                                <label htmlFor="lastName" className="block text-900 font-medium mb-2">
                                    Apellido
                                </label>
                                <InputText
                                    id="lastName"
                                    placeholder="Tu apellido"
                                    value={formData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                    className={`w-full ${errors.lastName ? 'p-invalid' : ''}`}
                                />
                                {errors.lastName && (
                                    <small className="p-error block mt-1">
                                        {errors.lastName}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="email" className="block text-900 font-medium mb-2">
                            Email
                        </label>
                        <InputText
                            id="email"
                            type="email"
                            placeholder="tu@email.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`w-full ${errors.email ? 'p-invalid' : ''}`}
                        />
                        {errors.email && (
                            <small className="p-error block mt-1">
                                {errors.email}
                            </small>
                        )}
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="password" className="block text-900 font-medium mb-2">
                            Contraseña
                        </label>
                        <Password
                            id="password"
                            placeholder="Tu contraseña"
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            className={`w-full ${errors.password ? 'p-invalid' : ''}`}
                            toggleMask
                        />
                        {errors.password && (
                            <small className="p-error block mt-1">
                                {errors.password}
                            </small>
                        )}
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="confirmPassword" className="block text-900 font-medium mb-2">
                            Confirmar Contraseña
                        </label>
                        <Password
                            id="confirmPassword"
                            placeholder="Confirma tu contraseña"
                            value={formData.confirmPassword}
                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                            className={`w-full ${errors.confirmPassword ? 'p-invalid' : ''}`}
                            feedback={false}
                            toggleMask
                        />
                        {errors.confirmPassword && (
                            <small className="p-error block mt-1">
                                {errors.confirmPassword}
                            </small>
                        )}
                    </div>

                    <div className="field mb-4">
                        <div className="flex align-items-start">
                            <Checkbox
                                id="acceptTerms"
                                checked={formData.acceptTerms}
                                onChange={(e) => handleInputChange('acceptTerms', e.checked)}
                                className={errors.acceptTerms ? 'p-invalid' : ''}
                                binary
                            />
                            <label htmlFor="acceptTerms" className="ml-2 text-900 line-height-3">
                                Acepto los{' '}
                                <Link to="/terms" className="text-primary hover:text-primary-600 no-underline">
                                    términos y condiciones
                                </Link>
                                {' '}y la{' '}
                                <Link to="/privacy" className="text-primary hover:text-primary-600 no-underline">
                                    política de privacidad
                                </Link>
                            </label>
                        </div>
                        {errors.acceptTerms && (
                            <small className="p-error block mt-1">
                                {errors.acceptTerms}
                            </small>
                        )}
                    </div>

                    <Button
                        type="submit"
                        label="Crear Cuenta"
                        className="w-full p-button-lg mb-4"
                        loading={loading}
                    />
                </form>

                <Divider align="center">
                    <span className="text-600 px-3">O regístrate con</span>
                </Divider>

                <div className="flex gap-2 mb-4">
                    <Button
                        label="Google"
                        icon="pi pi-google"
                        className="flex-1 p-button-outlined"
                        onClick={() => handleSocialRegister('Google')}
                    />
                    <Button
                        label="Facebook"
                        icon="pi pi-facebook"
                        className="flex-1 p-button-outlined"
                        onClick={() => handleSocialRegister('Facebook')}
                    />
                </div>

                <div className="text-center">
                    <span className="text-600">¿Ya tienes una cuenta? </span>
                    <Link 
                        to="/login" 
                        className="text-primary hover:text-primary-600 no-underline font-medium"
                    >
                        Inicia sesión aquí
                    </Link>
                </div>
            </Card>
        </div>
    );
};

export default RegisterForm;
